/**
 * Analítica — issue #44.
 *
 * Tendencias de escaneo, penetración por región y tasa de autenticación, todo
 * a partir de dos endpoints agregados: `GET /analytics/overview` y
 * `GET /analytics/timeseries`. Igual que el mapa, acá no hay ni una fila por
 * escaneo: el tenant ve agregados (regla 5 del dominio).
 *
 * Fechas y números salen siempre de `fmt`, que usa el locale y el huso del
 * **tenant**. Es lo que pide el issue explícitamente, y el motivo es concreto:
 * un `03/04` leído como `MM/DD` por un analista con el navegador en inglés
 * abre un caso sobre el día equivocado.
 *
 * El botón de recalcular está detrás de `analyst` porque el refresco recorre
 * `scan_event` del tenant; la API además le pone un enfriamiento de 60 s y
 * devuelve `refresh_cooldown`, que acá se muestra tal cual en vez de
 * esconderse: quien lo apretó tiene que entender que el dato ya estaba fresco,
 * no que el botón falló.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import { analyticsKeys } from '@/api/keys';
import { useApi } from '@/api/useApi';
import type { Schemas } from '@/api/types';
import { useAuth } from '@/auth/useAuth';
import type { MessageKey } from '@/i18n/messages';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState, LoadingState, QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { AnalyticsTabs } from './AnalyticsTabs';
import { chartSeries, type OverviewResponse, type TimeseriesResponse } from './series';
import { TrendChart } from './TrendChart';

type RefreshResult = Schemas['RefreshOut'];

/** Períodos ofrecidos. El de 365 días es el rango del informe anual (#44). */
const PERIODS = [7, 30, 90, 180, 365] as const;
type Period = (typeof PERIODS)[number];

const STALE_MS = 60_000;

export function AnalyticsPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const [days, setDays] = useState<Period>(90);

  const window = useMemo(() => ({ days }), [days]);
  const overview = useApiQuery<OverviewResponse>(
    analyticsKeys.overview(window),
    '/api/v1/analytics/overview',
    { days },
    { staleTime: STALE_MS },
  );
  const timeseries = useApiQuery<TimeseriesResponse>(
    analyticsKeys.timeseries(window),
    '/api/v1/analytics/timeseries',
    { days },
    { staleTime: STALE_MS },
  );

  const refresh = useMutation({
    mutationFn: (): Promise<RefreshResult> =>
      api.post<RefreshResult>(`/api/v1/analytics/refresh?days=${days}`),
    onSuccess: () => {
      // Los tres recursos —overview, timeseries y las celdas del mapa— salen
      // de los mismos rollups, así que se invalida el prefijo entero.
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    },
  });

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <AnalyticsTabs />

      <Card>
        <CardHeader
          title={t('analytics.title')}
          description={t('analytics.description')}
          actions={
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs font-medium text-ink-muted">
                {t('analytics.period')}
                <select
                  className="h-8 rounded border border-border-strong bg-surface px-2 text-sm text-ink"
                  value={days}
                  onChange={(event) => {
                    setDays(Number(event.target.value) as Period);
                  }}
                >
                  {PERIODS.map((option) => (
                    <option key={option} value={option}>
                      {t('analytics.days', { days: option })}
                    </option>
                  ))}
                </select>
              </label>
              {can('analyst') && (
                <Button
                  size="sm"
                  disabled={refresh.isPending}
                  onClick={() => {
                    refresh.mutate();
                  }}
                >
                  {refresh.isPending ? t('analytics.refreshing') : t('analytics.refresh')}
                </Button>
              )}
            </div>
          }
        />

        {refresh.isError && (
          <CardBody>
            <ErrorState error={refresh.error} />
          </CardBody>
        )}
        {refresh.isSuccess && (
          <CardBody className="border-b border-border text-xs text-ink-muted">
            {t('analytics.refreshed', {
              rows: fmt.number(refresh.data.rows_written),
              ms: fmt.number(refresh.data.duration_ms),
            })}
          </CardBody>
        )}

        <QueryBoundary query={overview} loadingRows={6}>
          {(data) => (
            <>
              <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Kpi label={t('analytics.kpi.scans')} value={fmt.number(data.total_scans)} />
                <Kpi
                  label={t('analytics.kpi.authRate')}
                  value={fmt.percent(
                    data.total_scans === 0 ? 0 : data.genuine_scans / data.total_scans,
                  )}
                  hint={t('analytics.kpi.authRateHint', {
                    genuine: fmt.compact(data.genuine_scans),
                  })}
                />
                <Kpi
                  label={t('analytics.kpi.suspiciousRate')}
                  value={fmt.percent(data.suspicious_rate, 3)}
                  hint={t('analytics.kpi.suspiciousHint', {
                    scans: fmt.number(data.suspicious_scans),
                  })}
                  tone={data.suspicious_scans > 0 ? 'critical' : 'neutral'}
                />
                <Kpi
                  label={t('analytics.kpi.openAlerts')}
                  value={fmt.number(data.open_alerts)}
                  hint={t('analytics.kpi.criticalHint', {
                    count: fmt.number(data.critical_open_alerts),
                  })}
                  tone={data.critical_open_alerts > 0 ? 'critical' : 'neutral'}
                />
              </CardBody>

              <CardBody className="border-t border-border text-xs text-ink-subtle">
                {t('analytics.window', {
                  from: fmt.date(data.from_day),
                  to: fmt.date(data.to_day),
                })}
                {' · '}
                {t('analytics.refreshedAt', {
                  when:
                    data.data_refreshed_at === null || data.data_refreshed_at === undefined
                      ? '—'
                      : fmt.dateTime(data.data_refreshed_at),
                })}
              </CardBody>
            </>
          )}
        </QueryBoundary>
      </Card>

      <Card>
        <CardHeader title={t('analytics.trend.title')} description={t('analytics.trend.description')} />
        {timeseries.isPending && <LoadingState rows={5} />}
        {timeseries.isError && (
          <CardBody>
            <ErrorState
              error={timeseries.error}
              onRetry={() => {
                void timeseries.refetch();
              }}
            />
          </CardBody>
        )}
        {timeseries.isSuccess && <Trends data={timeseries.data} />}
      </Card>

      <QueryBoundary query={overview} loadingRows={4}>
        {(data) => (
          <>
            <RegionTable overview={data} />
            <ResultTable overview={data} />
          </>
        )}
      </QueryBoundary>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── los gráficos

function Trends({ data }: { data: TimeseriesResponse }): ReactNode {
  const { t, fmt } = useI18n();
  // Acá está el criterio de aceptación de #44: la respuesta de doce meses son
  // hasta 2.555 filas y de acá salen como mucho 90 puntos.
  const buckets = useMemo(() => chartSeries(data), [data]);
  const totals = useMemo(
    () =>
      buckets.reduce(
        (acc, bucket) => ({
          scans: acc.scans + bucket.scans,
          suspicious: acc.suspicious + bucket.suspicious,
        }),
        { scans: 0, suspicious: 0 },
      ),
    [buckets],
  );

  const grain =
    buckets.length === 0
      ? 1
      : Math.max(...buckets.map((bucket) => bucket.days));

  return (
    <>
      <CardBody className="grid gap-6 lg:grid-cols-2">
        <TrendChart
          buckets={buckets}
          metric="scans"
          tone="accent"
          title={t('analytics.trend.scans')}
          summary={t('analytics.trend.scansSummary', {
            points: buckets.length,
            total: fmt.number(totals.scans),
          })}
        />
        <TrendChart
          buckets={buckets}
          metric="suspicious"
          tone="critical"
          title={t('analytics.trend.suspicious')}
          summary={t('analytics.trend.suspiciousSummary', {
            points: buckets.length,
            total: fmt.number(totals.suspicious),
          })}
        />
      </CardBody>

      <CardBody className="border-t border-border">
        <details>
          <summary className="cursor-pointer text-sm text-ink-muted">
            {t('analytics.trend.showData')}
          </summary>
          <div className="mt-3">
            <Table caption={t('analytics.trend.tableCaption')}>
              <thead>
                <tr>
                  <Th>{t('analytics.trend.column.period')}</Th>
                  <Th className="text-right">{t('analytics.trend.column.scans')}</Th>
                  <Th className="text-right">{t('analytics.trend.column.genuine')}</Th>
                  <Th className="text-right">{t('analytics.trend.column.suspicious')}</Th>
                  <Th className="text-right">{t('analytics.trend.column.units')}</Th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((bucket) => (
                  <tr key={bucket.from}>
                    <Td className="whitespace-nowrap">
                      {bucket.days > 1
                        ? `${fmt.date(bucket.from)} – ${fmt.date(bucket.to)}`
                        : fmt.date(bucket.from)}
                    </Td>
                    <Td className="text-right tabular-nums">{fmt.number(bucket.scans)}</Td>
                    <Td className="text-right tabular-nums text-ink-muted">
                      {fmt.number(bucket.genuine)}
                    </Td>
                    <Td className="text-right tabular-nums">
                      {bucket.suspicious > 0 ? (
                        <span className="text-critical">{fmt.number(bucket.suspicious)}</span>
                      ) : (
                        <span className="text-ink-subtle">—</span>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums text-ink-muted">
                      {fmt.number(bucket.units)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </details>
      </CardBody>

      <CardBody className="border-t border-border text-xs text-ink-subtle">
        {t('analytics.trend.grain', { days: grain, points: buckets.length })}
      </CardBody>
    </>
  );
}

// ───────────────────────────────────────────────────────────── las dos tablas

function RegionTable({ overview }: { overview: OverviewResponse }): ReactNode {
  const { t, fmt } = useI18n();
  const total = overview.total_scans;

  return (
    <Card>
      <CardHeader
        title={t('analytics.regions.title')}
        description={t('analytics.regions.description')}
      />
      <Table caption={t('analytics.regions.description')}>
        <thead>
          <tr>
            <Th>{t('analytics.regions.country')}</Th>
            <Th className="text-right">{t('analytics.regions.scans')}</Th>
            <Th className="text-right">{t('analytics.regions.share')}</Th>
            <Th className="text-right">{t('analytics.regions.suspicious')}</Th>
          </tr>
        </thead>
        <tbody>
          {overview.top_countries.map((country) => (
            <tr key={country.country_code}>
              <Td className="font-medium">{country.country_code}</Td>
              <Td className="text-right tabular-nums">{fmt.number(country.scans)}</Td>
              <Td className="text-right tabular-nums text-ink-muted">
                {fmt.percent(total === 0 ? 0 : country.scans / total)}
              </Td>
              <Td className="text-right">
                {country.suspicious > 0 ? (
                  <Badge tone="critical">{fmt.number(country.suspicious)}</Badge>
                ) : (
                  <span className="text-ink-subtle">—</span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

/**
 * Etiquetas de `scan_event.result`. Las claves son las de `RESULT_LABELS` en
 * `services/api/analytics/queries.py`. Se indexa por `string` a propósito: un
 * resultado nuevo en el backend tiene que caer al `label` crudo, no dejar la
 * celda vacía.
 */
const RESULT_KEYS: Record<string, MessageKey> = {
  genuine: 'analytics.result.genuine',
  already_seen: 'analytics.result.alreadySeen',
  not_activated: 'analytics.result.notActivated',
  invalid: 'analytics.result.invalid',
  stolen: 'analytics.result.stolen',
  recalled: 'analytics.result.recalled',
  out_of_territory: 'analytics.result.outOfTerritory',
};

function ResultTable({ overview }: { overview: OverviewResponse }): ReactNode {
  const { t, fmt } = useI18n();
  const total = overview.total_scans;

  return (
    <Card>
      <CardHeader
        title={t('analytics.results.title')}
        description={t('analytics.results.description')}
      />
      <Table caption={t('analytics.results.description')}>
        <thead>
          <tr>
            <Th>{t('analytics.results.result')}</Th>
            <Th className="text-right">{t('analytics.results.scans')}</Th>
            <Th className="text-right">{t('analytics.results.share')}</Th>
          </tr>
        </thead>
        <tbody>
          {overview.by_result.map((row) => {
            const key = RESULT_KEYS[row.label];
            return (
              <tr key={row.result}>
                <Td className="font-medium">{key === undefined ? row.label : t(key)}</Td>
                <Td className="text-right tabular-nums">{fmt.number(row.scans)}</Td>
                <Td className="text-right tabular-nums text-ink-muted">
                  {fmt.percent(total === 0 ? 0 : row.scans / total)}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────── piezas

function Kpi({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'critical';
}): ReactNode {
  return (
    <div>
      <p className="text-xs text-ink-muted">{label}</p>
      <p
        // La de titulares en la cifra grande: es el único número del dashboard que se lee
        // como titular. Los contadores de la bandeja y del mapa siguen en la
        // tipografía de interfaz, donde lo que importa es que la columna alinee.
        className={`font-display text-3xl font-semibold tabular-nums ${tone === 'critical' ? 'text-critical' : 'text-ink'}`}
      >
        {value}
      </p>
      {hint !== undefined && <p className="mt-0.5 text-xs text-ink-subtle">{hint}</p>}
    </div>
  );
}
